import type { MenuItem } from 'primeng/api';
import { PROJECT_CONSTANTS } from '../../../../../usermgmt/src/lib/constants/proj.cnst';
import { UM_TABLE_CNST } from '../../../../../usermgmt/src/lib/constants/table.cnst';
import {
  getApplicationResources,
  hasApplicationAccess,
  hasResourceAccess,
  parseUserPermissions
} from '../../../../../usermgmt/src/lib/shared/permission.util';
import { RESOURCE_TYPES, USER_ROLES } from '../constants/proj.cnst';

export const UM_MENU_RESOURCE_MAP: Record<string, string> = {
  Users: UM_TABLE_CNST.RESOURCES_LIST.user,
  Roles: UM_TABLE_CNST.RESOURCES_LIST.profile,
  Applications: UM_TABLE_CNST.RESOURCES_LIST.application,
  Organizations: UM_TABLE_CNST.RESOURCES_LIST.customers
};

export interface MenuPermissionOptions {
  isMobileView?: boolean;
}

export function getDashboardResources(permissions: any): any[] {
  const resources = getApplicationResources(permissions, PROJECT_CONSTANTS.APPLICATION_NAME);
  return Object.values(resources).filter(
    (resource: any) => resource?.resourceTypeName === RESOURCE_TYPES.DASHBOARDS
  );
}

export function isDashboardMenuItem(item: any): boolean {
  return item?.resourceTypeName === RESOURCE_TYPES.DASHBOARDS;
}

export function isUserManagementMenuItem(item: any): boolean {
  return item?.resourceName === PROJECT_CONSTANTS.USERMGMT_APP_NAME
    || item?.label === PROJECT_CONSTANTS.USERMGMT_APP_NAME;
}

export function isMenuResourceAllowed(item: any, availableDashboardResources: any[] | null): boolean {
  if (!isDashboardMenuItem(item)) {
    return true;
  }
  if (!availableDashboardResources) {
    return false;
  }
  return availableDashboardResources.some(
    (resource: any) => resource?.resourceName === item?.resourceName
  );
}

export function filterMenuTree(menuItems: MenuItem[], availableDashboardResources: any[] | null): MenuItem[] {
  if (!Array.isArray(menuItems)) {
    return [];
  }

  return menuItems.reduce((acc: MenuItem[], item: any) => {
    if (item?.items && item.items.length > 0) {
      const filteredChildren = filterMenuTree(item.items, availableDashboardResources);
      if (filteredChildren.length > 0) {
        acc.push({ ...item, items: filteredChildren });
      }
    } else if (isMenuResourceAllowed(item, availableDashboardResources)) {
      acc.push(item);
    }
    return acc;
  }, []);
}

export function filterUserManagementMenuItems(menuItems: MenuItem[], permissions: any): MenuItem[] {
  return menuItems.reduce((acc: MenuItem[], item: any) => {
    if (isUserManagementMenuItem(item) && Array.isArray(item.items)) {
      const filteredChildren = item.items.filter((subItem: any) => {
        const resourceKey = UM_MENU_RESOURCE_MAP[subItem?.label];
        if (!resourceKey) {
          return true;
        }
        const umResources = getApplicationResources(permissions, PROJECT_CONSTANTS.USERMGMT_APP_NAME);
        if (Object.keys(umResources).length === 0) {
          return true;
        }
        return hasResourceAccess(permissions, resourceKey, PROJECT_CONSTANTS.USERMGMT_APP_NAME);
      });

      if (filteredChildren.length > 0) {
        acc.push({ ...item, items: filteredChildren });
      }
      return acc;
    }

    if (item?.items && item.items.length > 0) {
      acc.push({ ...item, items: filterUserManagementMenuItems(item.items, permissions) });
      return acc;
    }

    acc.push(item);
    return acc;
  }, []);
}

export function filterAccessibleMenuItems(
  menuItems: MenuItem[],
  permissionsInput: any,
  options: MenuPermissionOptions = {}
): MenuItem[] {
  const permissions = parseUserPermissions(permissionsInput);
  const clonedItems: MenuItem[] = JSON.parse(JSON.stringify(menuItems || []));

  if (Object.keys(permissions).length === 0) {
    return clonedItems.filter((item: any) => item?.resourceName === 'Home');
  }

  let items = clonedItems;
  const hasUmAccess = hasApplicationAccess(permissions, PROJECT_CONSTANTS.USERMGMT_APP_NAME);

  if (options.isMobileView || !hasUmAccess) {
    items = items.filter((item: any) => !isUserManagementMenuItem(item));
  } else {
    const userRole = permissions[PROJECT_CONSTANTS.USERMGMT_APP_NAME]?.roleName;
    if (userRole !== USER_ROLES.SUPER_ADMIN) {
      items = items.map((item: any) => {
        if (isUserManagementMenuItem(item) && Array.isArray(item.items)) {
          return {
            ...item,
            items: item.items.filter((subItem: any) => subItem?.label !== 'Organizations')
          };
        }
        return item;
      });
    }
    items = filterUserManagementMenuItems(items, permissions);
  }

  const hasApcrdaAccess = hasApplicationAccess(permissions, PROJECT_CONSTANTS.APPLICATION_NAME);
  const dashboardResources = hasApcrdaAccess ? getDashboardResources(permissions) : null;

  return filterMenuTree(items, dashboardResources);
}
